import { useMemo } from 'react';
import {
  TaskCategory,
  PriorityLevel,
  SpendAreaStage,
  STEP_ACTION_TITLE_MAX_LENGTH,
  STEP_ACTION_DESCRIPTION_MAX_LENGTH,
  ActionItemType,
  TASK_DESCRIPTION_MAX_LENGTH,
  TASK_TITLE_MAX_LENGTH,
  TASK_REMINDER_MAX_LENGTH,
  TASK_COMMENT_MAX_LENGTH,
  TEAM_TITLE_MAX_LENGTH,
  TEAM_SUBTITLE_MAX_LENGTH,
  TICKET_NOTE_MAX_LENGTH,
  SubauditStage,
  TicketStatus,
  TaskStatus,
  TicketCommentScope,
  TicketCategory,
  TagType,
} from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { z } from 'zod';

export enum ValidationRule {
  TaskTitle = 'taskTitle',
  TaskDescription = 'taskDescription',
  PriorityLevel = 'priorityLevel',
  ProperDate = 'properDate',
  TaskCategory = 'taskCategory',
  ReferenceLink = 'referenceLink',
  ReferenceName = 'referenceName',
  Stage = 'stage',
  TaskTag = 'taskTag',
  TaskAssignee = 'taskAssignee',
  ActionTitle = 'actionTitle',
  ActionDescription = 'actionDescription',
  ActionType = 'actionType',
  CommentMessage = 'commentMessage',
  TaskReminderPriority = 'taskReminderPriority',
  TaskReminderMessage = 'taskReminderMessage',
  InviteTeamMember = 'inviteTeamMember',
  TeamCreationName = 'teamCreation',
  TeamCreationSubtitle = 'teamCreationSubtitle',
  TeamCreationMembers = 'teamCreationMembers',
  SubAuditName = 'subAuditName',
  SubAuditCommentTopic = 'subAuditCommentTopic',
  Owner = 'owner',
  Organization = 'organization',
  Audit = 'audit',
  Vendor = 'vendor',
  VendorAccount = 'vendorAccount',
  SpendAmount = 'spendAmount',
  Team = 'team',
  Localization = 'localization',
  SubAuditStage = 'subAuditStage',
  OnHold = 'onHold',
  OnHoldDueDate = 'onHoldDueDate',
  OnHoldReason = 'onHoldReason',
  TicketNote = 'ticketNote',
  IsInternalTicketNote = 'isInternalTicketNote',
  TicketStatus = 'ticketStatus',
  TaskStatus = 'taskStatus',
  TicketVendor = 'ticketVendor',
  TicketAssignee = 'ticketAssignee',
  TicketPriority = 'ticketPriority',
  TicketCategory = 'ticketCategory',
  TaskLogTime = 'taskLogTime',
  TaskEstimatedTime = 'taskEstimatedTime',
  TicketSubject = 'ticketSubject',
  TicketDescription = 'ticketDescription',
  TicketOrganization = 'ticketOrganization',
  TicketScope = 'ticketScope',
}

export interface ValidationSchemaField {
  optional?: boolean;
  rule: `${ValidationRule}`;
  name: string;
}

export const useValidationSchema = () => {
  const { t } = useTranslation();

  const rules = useMemo(
    () => ({
      [ValidationRule.ActionTitle]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z
            .string()
            .min(1, {
              message: t('errors.validation.required', {
                fieldLabel: t('fields.title'),
              }),
            })
            .max(STEP_ACTION_TITLE_MAX_LENGTH, {
              message: t('errors.validation.cantBeLonger', {
                fieldLabel: t('fields.title'),
                maxLength: STEP_ACTION_TITLE_MAX_LENGTH,
              }),
            })
        ),
      [ValidationRule.ActionDescription]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z.string().max(STEP_ACTION_DESCRIPTION_MAX_LENGTH, {
            message: t('errors.validation.cantBeLonger', {
              fieldLabel: t('fields.description'),
              maxLength: STEP_ACTION_DESCRIPTION_MAX_LENGTH,
            }),
          })
        ),
      [ValidationRule.TaskTitle]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z
            .string()
            .min(1, {
              message: t('errors.validation.required', {
                fieldLabel: t('fields.title'),
              }),
            })
            .max(TASK_TITLE_MAX_LENGTH, {
              message: t('errors.validation.cantBeLonger', {
                fieldLabel: t('fields.title'),
                maxLength: TASK_TITLE_MAX_LENGTH,
              }),
            })
        ),

      [ValidationRule.TaskDescription]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z.string().max(TASK_DESCRIPTION_MAX_LENGTH, {
            message: t('errors.validation.cantBeLonger', {
              fieldLabel: t('fields.description'),
              maxLength: TASK_DESCRIPTION_MAX_LENGTH,
            }),
          })
        ),
      [ValidationRule.PriorityLevel]: z.enum(PriorityLevel),
      [ValidationRule.ActionType]: z.enum(ActionItemType),
      [ValidationRule.Stage]: z.enum(SpendAreaStage),
      [ValidationRule.ProperDate]: z
        .date()
        .refine(value => value instanceof Date && !isNaN(value.getTime()), {
          message: t('errors.validation.invalidDate'),
        }),
      [ValidationRule.TaskTag]: z.object({
        [TagType.Custom]: z.array(z.string()),
        [TagType.Contact]: z.array(z.string()),
        [TagType.Employee]: z.array(z.string()),
      }),
      [ValidationRule.TaskAssignee]: z.string(),
      [ValidationRule.TaskCategory]: z.enum(TaskCategory),
      [ValidationRule.ReferenceLink]: z.string(),
      [ValidationRule.ReferenceName]: z.string(),
      [ValidationRule.CommentMessage]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z
            .string()
            .min(1, {
              message: t('errors.validation.required', {
                fieldLabel: t('fields.comment'),
              }),
            })
            .max(TASK_COMMENT_MAX_LENGTH, {
              message: t('errors.validation.cantBeLonger', {
                fieldLabel: t('fields.comment'),
                maxLength: TASK_COMMENT_MAX_LENGTH,
              }),
            })
        ),
      [ValidationRule.TaskReminderPriority]: z.enum(PriorityLevel),
      [ValidationRule.TaskReminderMessage]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z
            .string()
            .min(1, {
              message: t('errors.validation.required', {
                fieldLabel: t('fields.reminderMessage'),
              }),
            })
            .max(TASK_REMINDER_MAX_LENGTH, {
              message: t('errors.validation.cantBeLonger', {
                fieldLabel: t('fields.reminderMessage'),
                maxLength: TASK_REMINDER_MAX_LENGTH,
              }),
            })
        ),
      [ValidationRule.InviteTeamMember]: z.array(z.string()).optional(),
      [ValidationRule.TeamCreationName]: z
        .string()
        .trim()
        .max(TEAM_TITLE_MAX_LENGTH)
        .min(1, {
          message: t('errors.validation.required', {
            fieldLabel: t('fields.teamName'),
          }),
        }),
      [ValidationRule.TeamCreationSubtitle]: z
        .string()
        .max(TEAM_SUBTITLE_MAX_LENGTH)
        .optional(),
      [ValidationRule.TeamCreationMembers]: z.array(z.string()).optional(),
      [ValidationRule.SubAuditName]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z.string().min(1, {
            message: t('errors.validation.required', {
              fieldLabel: t(
                'components.subAuditForm.sections.details.fields.subAuditName.label'
              ),
            }),
          })
        ),
      [ValidationRule.SubAuditCommentTopic]: z.string(),
      [ValidationRule.Owner]: z.string(),
      [ValidationRule.Organization]: z.string(),
      [ValidationRule.Audit]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z.string().min(1, {
            message: t('errors.validation.required', {
              fieldLabel: t(
                'components.subAuditForm.sections.details.fields.audit.label'
              ),
            }),
          })
        ),
      [ValidationRule.Vendor]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z.string().min(1, {
            message: t('errors.validation.required', {
              fieldLabel: t(
                'components.subAuditForm.sections.details.fields.vendor.label'
              ),
            }),
          })
        ),
      [ValidationRule.VendorAccount]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z.string().min(1, {
            message: t('errors.validation.required', {
              fieldLabel: t(
                'components.subAuditForm.sections.details.fields.vendorAccount.label'
              ),
            }),
          })
        ),
      [ValidationRule.SpendAmount]: z.coerce
        .number()
        .transform(val => Number(val))
        .refine(val => !isNaN(val))
        .refine(val => val >= 0, {
          message: t('errors.validation.positiveNumber', {
            fieldLabel: t(
              'components.subAuditForm.sections.details.fields.spend.label'
            ),
          }),
        }),
      [ValidationRule.Team]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z.string().min(1, {
            message: t('errors.validation.required', {
              fieldLabel: t(
                'components.subAuditForm.sections.details.fields.team.label'
              ),
            }),
          })
        ),
      [ValidationRule.Localization]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z.string().min(1, {
            message: t('errors.validation.required', {
              fieldLabel: t(
                'components.subAuditForm.sections.details.fields.localization.label'
              ),
            }),
          })
        ),
      [ValidationRule.SubAuditStage]: z.enum(SubauditStage),
      [ValidationRule.OnHold]: z.boolean(),
      [ValidationRule.OnHoldDueDate]: z
        .date()
        .refine(value => value instanceof Date && !isNaN(value.getTime()), {
          message: t('errors.validation.invalidDate'),
        }),
      [ValidationRule.OnHoldReason]: z.string().optional(),
      [ValidationRule.TicketNote]: z
        .string()
        .min(1, {
          message: t('errors.validation.required', {
            fieldLabel: t('components.ticketListItem.fields.note'),
          }),
        })
        .max(TICKET_NOTE_MAX_LENGTH, {
          message: t('errors.validation.cantBeLonger', {
            fieldLabel: t('components.ticketListItem.fields.note'),
            maxLength: TICKET_NOTE_MAX_LENGTH,
          }),
        }),
      [ValidationRule.IsInternalTicketNote]: z
        .boolean()
        .default(false)
        .transform(value =>
          value ? TicketCommentScope.Internal : TicketCommentScope.External
        ),
      [ValidationRule.TicketStatus]: z.enum(TicketStatus),
      [ValidationRule.TaskStatus]: z.enum(TaskStatus),
      [ValidationRule.TicketVendor]: z.string().optional().nullable(),
      [ValidationRule.TicketAssignee]: z.string().optional(),
      [ValidationRule.TicketPriority]: z.enum(PriorityLevel),
      [ValidationRule.TicketCategory]: z.enum(TicketCategory),
      [ValidationRule.TicketSubject]: z
        .string()
        .transform(value => value.trim())
        .pipe(
          z.string().min(1, {
            message: t('errors.validation.required', {
              fieldLabel: t('components.ticketForm.fields.subject'),
            }),
          })
        ),
      [ValidationRule.TicketDescription]: z.string().optional(),
      [ValidationRule.TicketOrganization]: z.string().pipe(
        z.string().min(1, {
          message: t('errors.validation.required', {
            fieldLabel: t('components.ticketForm.fields.organization.label'),
          }),
        })
      ),
      [ValidationRule.TicketScope]: z.boolean().default(false),
      [ValidationRule.TaskLogTime]: z.coerce
        .number()
        .int({
          message: t('errors.validation.integer', {
            fieldLabel: t('fields.time'),
          }),
        })
        .min(1, {
          message: t('errors.validation.positiveNumber', {
            fieldLabel: t('fields.estimatedTime'),
          }),
        })
        .transform(val => Number(val))
        .refine(val => !isNaN(val)),
      [ValidationRule.TaskEstimatedTime]: z.coerce
        .number()
        .transform(val => (val > 0 ? val : undefined))
        .pipe(
          z
            .number()
            .int({
              message: t('errors.validation.integer', {
                fieldLabel: t('fields.estimatedTime'),
              }),
            })
            .min(1, {
              message: t('errors.validation.positiveNumber', {
                fieldLabel: t('fields.estimatedTime'),
              }),
            })
            .optional()
        ),
    }),
    [t]
  );

  const buildShema = (fields: ValidationSchemaField[]) => {
    const schema = fields.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: rules[field.rule],
      }),
      {}
    );

    return z.object(schema);
  };

  return { buildShema, rules };
};
